import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import JiraTicketModal from './JiraTicketModal';
import { analyzeBugWithRegression } from '../../services/aiBugAnalysisService';

export default function AIBugAnalysisDialog({ open, onClose, run }) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [analysisData, setAnalysisData] = useState(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);

  const handleAnalyzeBug = async () => {
    try {
      setIsAnalyzing(true);
      setAnalysisError('');

      const response = await analyzeBugWithRegression(run.runId);

      setAnalysisData(response);
    } catch (error) {
      setAnalysisError(error?.response?.data?.message || error.message || 'Failed to analyze bug.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  useEffect(() => {
    if (open && run?.runId) {
      setAnalysisData(null);
      setAnalysisError('');
      handleAnalyzeBug();
    }
  }, [open, run?.runId]);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <BugReportIcon color="error" />
            <Typography variant="h6">AI Bug Analysis</Typography>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isAnalyzing && !analysisData && (
            <Box display="flex" justifyContent="center" alignItems="center" py={5}>
              <CircularProgress />
            </Box>
          )}

          {analysisError && <Alert severity="error">{analysisError}</Alert>}

          {analysisData && (
            <Box display="flex" flexDirection="column" gap={2}>
              {isAnalyzing && <Alert severity="info">Refreshing AI analysis...</Alert>}

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderLeft: '4px solid',
                  borderColor: 'error.main',
                }}
              >
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Root Cause
                </Typography>

                <Typography variant="body2">
                  {analysisData.root_cause || 'No root cause available.'}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Secondary Errors
                </Typography>

                {analysisData.secondary_errors?.length ? (
                  <List dense>
                    {analysisData.secondary_errors.map((item, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemText primary={`• ${item}`} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No secondary errors.
                  </Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Possible Causes
                </Typography>

                {analysisData.possible_causes?.length ? (
                  <List dense>
                    {analysisData.possible_causes.map((item, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemText primary={`• ${item}`} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No possible causes available.
                  </Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Suggested Fixes
                </Typography>

                {analysisData.suggested_fixes?.length ? (
                  <List dense>
                    {analysisData.suggested_fixes.map((item, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemText primary={`• ${item}`} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No suggested fixes available.
                  </Typography>
                )}
              </Paper>

              <Box display="flex" justifyContent="space-between" mt={2}>
                <Button variant="outlined" onClick={handleAnalyzeBug} disabled={isAnalyzing}>
                  {isAnalyzing ? (
                    <>
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                      Re-Analyzing...
                    </>
                  ) : (
                    'Re-Analyze'
                  )}
                </Button>

                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setTicketModalOpen(true)}
                  disabled={isCreatingTicket || isAnalyzing}
                >
                  Create Jira Ticket
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <JiraTicketModal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        analysisData={analysisData}
        isCreatingTicket={isCreatingTicket}
        setIsCreatingTicket={setIsCreatingTicket}
      />
    </>
  );
}
